// Pull-to-refresh plumbing. The gesture lives in PullToRefresh; this is how
// the rest of the app hears about it.
//
// A pull remounts the current page (App bumps the key on <Routes>), so every
// page refetches through its own mount effects — no per-page wiring needed.
// Long-lived providers above the routes don't remount, so they listen here.

export const APP_REFRESH_EVENT = 'cockpit:refresh';

export function emitAppRefresh() {
  window.dispatchEvent(new Event(APP_REFRESH_EVENT));
}

export function onAppRefresh(cb: () => void): () => void {
  window.addEventListener(APP_REFRESH_EVENT, cb);
  return () => window.removeEventListener(APP_REFRESH_EVENT, cb);
}

/** True when a newer build has been deployed than the one running. Compares
 *  the entry script Vite stamps into index.html (hashed in production). */
export async function hasNewerBuild(): Promise<boolean> {
  try {
    const current = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')?.getAttribute('src');
    if (!current) return false; // dev server: no hashed entry
    const res = await fetch('/', { cache: 'no-store', headers: { accept: 'text/html' } });
    if (!res.ok) return false;
    const html = await res.text();
    const m = html.match(/<script[^>]+type="module"[^>]+src="([^"]+)"/);
    return !!m && m[1] !== current;
  } catch {
    return false;
  }
}

/** Standalone = launched from the home screen, where there is no browser
 *  chrome and so no other way to refresh. `localStorage['cockpit:ptr'] = 'on'`
 *  forces it on in a browser tab (testing). */
export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    nav.standalone === true ||
    window.matchMedia?.('(display-mode: standalone)').matches ||
    localStorage.getItem('cockpit:ptr') === 'on'
  );
}
